import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { AmwayFilledLogoIcon } from "../../../../common/icons/AmwayFilledLogoIcon/AmwayFilledLogoIcon";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import LeafTag from "../../../../common/leaf/LeafTag/LeafTag";
import { getCountryFlag } from "../../../../common/util/getCountryFlag";
import { QuickContactIcons } from "../QuickContactIcons/QuickContactIcons";
import defaultContent from "../../../../content/actionReports";
import "./ActionReportProfileSection.scss";
import { CUSTOMER_BUSINESS_NATURES } from "../../../../common/enums/businessNatures";
import DownlineProfileIcon from "../../../../common/components/DownlineProfileLOS/DownlineProfileIcon";
import type { KpiId } from "../../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { useAppSelector } from "../../../../store";
import maskAboNumber from "../../../../common/util/maskAboNumber";

export type ProfileSectionTags = {
  isProvisional?: boolean;
  isFirstContractYear?: boolean;
  businessNatureTag?: "abo" | "member" | "customer" | "guestCustomer";
  conversionType?: string;
  upForRenewal?: boolean;
  level?: number;
  businessNature?: string;
  /** When true, the "hide tag for customers deeper than level 2" rule does not apply (Renewals only). */
  showTagForDeepCustomers?: boolean;
  /** Whether this row is authorized to show deeper customer details. */
  allowCustomerDetails?: boolean;
};

export type ActionReportProfileSectionProps = {
  /** Display name of the ABO/Customer */
  aboName: ReactNode;
  /** ABO number (without # prefix) */
  aboNumber: string;
  /** Aff number for the LOS lineage chevron; omit (or when the row is a customer) to hide it */
  aff?: string;
  /** Abo number for the LOS lineage chevron; omit (or when the row is a customer) to hide it */
  abo?: string;
  /** Click handler invoked with the CLICKED lineage pill's own aff-abo (may differ from this row's aff/abo) */
  aboOnclick?: (affAbo: string) => void;
  /** Whether to request Global Market Builder data for the LOS lineage fetch (Watchlist only) */
  includeGlobalMB?: boolean;
  /** KPIs to request for the LOS lineage popup; defaults to LosLineage's own default set when omitted */
  kpis?: KpiId[];
  /** Rank/position badge number shown in the blue square */
  rankBadge?: number | string;
  /** URL for the profile photo; falls back to default avatar when not provided */
  photoUrl?: string;
  /** Whether this is a China ABO (disables interactions) */
  isChina?: boolean;
  /** Whether this is an international ABO */
  isInternational?: boolean;
  /** Country code for international flag display */
  countryCode?: string;
  /** Optional tags (provisional, FCY) — shown on desktop only */
  tags?: ProfileSectionTags;
  /** Watchlist state; omit to hide the toggle */
  watchlist?: boolean;
  /** Callback to toggle watchlist; omit to hide the toggle */
  toggleWatchlist?: () => void;
  /** Click handler for the ABO name */
  onAboNameClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Whether the row is in a loading state */
  isLoading?: boolean;
  /** Whether this report permits full customer details for deeper rows */
  allowCustomerDetails?: boolean;
  /** Phone number for quick contact icons */
  phoneNumber?: string;
  onPhoneTrack?: () => void;
  onMessageTrack?: () => void;
  /** WhatsApp number for quick contact icons */
  whatsAppNumber?: string;
  /** Independent loading state for the WhatsApp icon; falls back to `isLoading` when omitted */
  whatsAppLoading?: boolean;
  onWhatsAppTrack?: () => void;
  /**
   * Show the dashed vertical connector line above the rank badge.
   * False for the first card in a list, true for all subsequent cards.
   */
  showDashedLine?: boolean;
  /** Whether to show the profile photo / user icon fallback. Defaults to false. */
  showImageBlock?: boolean;
  /**
   * Layout variant.
   * - "desktop": photo row (badge+photo+tags) → name → number → actions (used in desktop table)
   * - "mobile":  two-col top (badge+photo | name+number) → actions full-width (used in mobile card)
   */
  variant?: "desktop" | "mobile";

  /**
   * Privacy Flag (Confidential Users)
   */
  privacyFlag?: boolean;
};

const BUSINESS_NATURE_TAG_CONFIG: Record<
  NonNullable<ProfileSectionTags["businessNatureTag"]>,
  { color: "blue" | "lightBlue"; labelKey: keyof typeof defaultContent }
> = {
  abo: { color: "blue", labelKey: "aboTag" },
  member: { color: "lightBlue", labelKey: "memberTag" },
  customer: { color: "lightBlue", labelKey: "customerTag" },
  guestCustomer: { color: "lightBlue", labelKey: "guestCustomerTag" },
};

export const ActionReportProfileSection = ({
  aboName,
  aboNumber,
  aff,
  abo,
  aboOnclick,
  includeGlobalMB,
  kpis,
  rankBadge,
  photoUrl,
  isChina = false,
  isInternational = false,
  countryCode,
  tags,
  watchlist,
  toggleWatchlist,
  onAboNameClick,
  isLoading = false,
  phoneNumber,
  onPhoneTrack,
  onMessageTrack,
  whatsAppNumber,
  whatsAppLoading,
  onWhatsAppTrack,
  allowCustomerDetails = false,
  showDashedLine = true,
  showImageBlock = false,
  variant = "desktop",
  privacyFlag = false,
}: ActionReportProfileSectionProps) => {
  const { t } = useTranslation(["actionReports", "leafAboName"]);
  const [photoError, setPhotoError] = useState(false);
  const [isLineageOpen, setIsLineageOpen] = useState(false);
  const isCustomerRow = CUSTOMER_BUSINESS_NATURES.has(Number(tags?.businessNature));
  const isMaskAboNumber =
    useAppSelector((s) => s.boot.configuration.actionReports.maskConfidentialAboNumber) && privacyFlag;

  const displayName =
    aboName === "Confidential User" || privacyFlag
      ? t("ConfidentialUser", "Confidential User", { ns: "leafAboName" })
      : aboName;

  const hasPhoto = !!photoUrl && !photoError;
  const showLosIcon = !isCustomerRow && !!aff && !!abo;

  const photoBlock = (
    <div
      className={`arProfileSection__badgeAndPhoto${isLineageOpen ? " arProfileSection__badgeAndPhoto--active" : ""}`}
    >
      {rankBadge != null && showDashedLine && !showLosIcon && (
        <div
          className={`arProfileSection__dashedLine${isCustomerRow ? " arProfileSection__dashedLine--customer" : ""}`}
          aria-hidden="true"
        >
          <span className="arProfileSection__dash" />
          <span className="arProfileSection__dash" />
          <span className="arProfileSection__dash" />
          <span className="arProfileSection__dash" />
        </div>
      )}
      {rankBadge != null && !showLosIcon && (
        <div
          className={`arProfileSection__rankBadge${isCustomerRow ? " arProfileSection__rankBadge--customer" : ""}`}
          aria-label={t("rankBadge", defaultContent.rankBadge, { rank: rankBadge })}
        >
          {rankBadge}
        </div>
      )}
      {showLosIcon && (
        <DownlineProfileIcon
          variant="list"
          aff={aff}
          abo={abo}
          aboOnclick={aboOnclick}
          onLineageOpenChange={setIsLineageOpen}
          includeGlobalMB={includeGlobalMB}
          kpis={kpis}
        />
      )}
      <div className={`arProfileSection__photo${showLosIcon ? " arProfileSection__photo--withLosIcon" : ""}`}>
        {hasPhoto ? (
          <img
            role="presentation"
            src={photoUrl}
            alt=""
            className="arProfileSection__photoImg"
            onError={() => setPhotoError(true)}
          />
        ) : (
          <div
            className={`arProfileSection__photoFallback${isCustomerRow ? " arProfileSection__photoFallback--customer" : ""}`}
            aria-hidden="true"
          >
            <AmwayFilledLogoIcon />
          </div>
        )}
      </div>
    </div>
  );

  const hideBusinessNatureTag =
    Number(tags?.level) > 2 &&
    CUSTOMER_BUSINESS_NATURES.has(Number(tags?.businessNature)) &&
    !allowCustomerDetails &&
    !tags?.showTagForDeepCustomers;

  const tagConfig = tags?.businessNatureTag ? BUSINESS_NATURE_TAG_CONFIG[tags.businessNatureTag] : null;

  const businessNatureTagBlock = hideBusinessNatureTag ? null : (
    <>
      {tagConfig && (
        <LeafTag variant={tagConfig.color}>
          {t(tagConfig.labelKey, defaultContent[tagConfig.labelKey] as string)}
        </LeafTag>
      )}
      {tags?.conversionType && tags?.conversionType !== "Rejoined" && (
        <LeafTag variant="gray">{t("migratedTag", defaultContent["migratedTag"] as string)}</LeafTag>
      )}
    </>
  );

  const hasTags =
    tags?.isProvisional ||
    tags?.isFirstContractYear === true ||
    tags?.upForRenewal === true ||
    (!hideBusinessNatureTag && (tagConfig || (tags?.conversionType && tags.conversionType !== "Rejoined")));

  const getNameContent = () => {
    if (displayName && typeof displayName === "string") {
      return <span className="arProfileSection__name">{displayName}</span>;
    }
    if (isLoading) {
      return <LeafRectangleLoader isLoading width="7.5rem" height="1.25rem" margin="0 0 0.25rem 0" />;
    }
    return null;
  };

  const nameBlock = (
    <>
      <button
        className={`arProfileSection__nameBtn${!showImageBlock && !hasTags ? " arProfileSection__nameBtn--offset" : ""}`}
        onClick={onAboNameClick}
        disabled={!onAboNameClick}
      >
        {getNameContent()}
      </button>
      {isInternational && countryCode && (
        <div className="arProfileSection__international">
          {getCountryFlag(countryCode)}{" "}
          {t("international", defaultContent["international"] as string, { ns: "actionReports" })}
        </div>
      )}
      <span className="arProfileSection__aboNumber">#{isMaskAboNumber ? maskAboNumber(aboNumber) : aboNumber}</span>
    </>
  );

  const actionsBlock = (
    <div className="arProfileSection__actions">
      <QuickContactIcons
        phoneNumber={phoneNumber}
        watchlist={watchlist}
        toggleWatchlist={toggleWatchlist}
        isLoading={isLoading}
        isChina={isChina}
        onPhoneTrack={onPhoneTrack}
        onMessageTrack={onMessageTrack}
        whatsAppNumber={whatsAppNumber}
        whatsAppLoading={whatsAppLoading}
        onWhatsAppTrack={onWhatsAppTrack}
      />
    </div>
  );

  const typeTagBlock = hasTags ? (
    <div
      className={`arProfileSection__typeTag${!showImageBlock && variant !== "mobile" ? " arProfileSection__typeTag--noPhoto" : ""}`}
    >
      {tags?.isProvisional && (
        <LeafTag variant="lightYellow">{t("provisionalTag", defaultContent["provisionalTag"] as string)}</LeafTag>
      )}
      {tags?.isFirstContractYear === true && (
        <LeafTag variant="purple">{t("fcyTag", defaultContent["fcyTag"] as string)}</LeafTag>
      )}
      {businessNatureTagBlock}
      {tags?.upForRenewal === true && (
        <LeafTag variant="orange">{t("sopUpForRenewalTag", defaultContent["sopUpForRenewalTag"] as string)}</LeafTag>
      )}
    </div>
  ) : null;

  if (variant === "mobile") {
    return (
      <div
        className={`arProfileSection arProfileSection--mobile${!showImageBlock ? " arProfileSection--noPhoto" : ""}`}
      >
        <div className="arProfileSection__topRow">
          {!isLoading && showImageBlock && photoBlock}
          <div
            className={`arProfileSection__nameCol${hasTags ? " arProfileSection__nameCol--hasTags" : ""}${!showImageBlock ? " arProfileSection__nameCol--noPhoto" : ""}${!showImageBlock && !hasTags ? " arProfileSection__nameCol--noPhotoNoTags" : ""}`}
          >
            {showImageBlock ? typeTagBlock : nameBlock}
            {showImageBlock ? nameBlock : typeTagBlock}
          </div>
        </div>
        {actionsBlock}
      </div>
    );
  }

  // Desktop variant — original stacked layout with type tag
  return (
    <div className={`arProfileSection${!showImageBlock ? " arProfileSection--noPhoto" : ""}`}>
      {/* Row 1: photo + tags (when photo enabled) or tags standalone */}
      {showImageBlock ? (
        <div className="arProfileSection__photoRow">
          {photoBlock}
          {typeTagBlock}
        </div>
      ) : (
        typeTagBlock
      )}
      {/* Row 2: name */}
      {nameBlock}
      {/* Row 3: actions */}
      {actionsBlock}
    </div>
  );
};
